import { useEffect, useState } from 'react';
import { Button, Form, Input, Toast } from 'antd-mobile';
import classNames from 'classnames';
import '@styles/views/login.less';
import $http from '@/http';
import { getSmsCode, setUserInfo } from '@store/appSlice';
import { useAppDispatch } from '@hooks/reduxHooks';

interface LoginForm {
  'loginForm.phone'?: string;
  'loginForm.password'?: string;
}

interface RegisterForm {
  'registerForm.phone'?: string;
  'registerForm.code'?: string;
  'registerForm.password'?: string;
  'registerForm.enterpriseCode'?: string;
}

const Login = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('login');
  const [codeTip, setCodeTip] = useState('获取验证码');
  const [registerForm] = Form.useForm<RegisterForm>();
  const [count, setCount] = useState(0);

  const loginRules: FormRule<LoginForm> = {
    'loginForm.phone': [
      { required: true, message: '手机号不能为空' },
      { pattern: /^1[3|4|5|7|8][0-9]{9}$/, message: '手机号格式不正确' },
    ],
    'loginForm.password': [{ required: true, message: '密码不能为空' }],
  };

  const registerRules: FormRule<RegisterForm> = {
    'registerForm.phone': [
      { required: true, message: '手机号不能为空' },
      { pattern: /^1[3|4|5|7|8][0-9]{9}$/, message: '手机号格式不正确' },
    ],
    'registerForm.code': [{ required: true, message: '验证码不能为空' }],
    'registerForm.password': [{ required: true, message: '密码不能为空' }],
  };

  /**
   * 登录
   * @param data
   */
  const login = (data: LoginForm) => {
    let params: {
      [propName: string]: string | undefined;
    } = {};

    Object.keys(data).forEach((key) => {
      params[key.split('.')[1]] = data[key as keyof LoginForm];
    });

    setLoading(true);

    $http
      .post<GetUserInfoRes>('api/Home/phoneLogin', params)
      .then((userInfoData) => {
        setLoading(false);
        dispatch(setUserInfo(userInfoData.data));

        Toast.show({
          icon: 'success',
          content: '登录成功',
          position: 'top',
        });

        // 获取过期时间
        $http.get<GetExpirationRes>('api/WebOrder/ServiceTimeVerify').then((expireData) => {
          dispatch(setUserInfo({ ...userInfoData.data, expireTime: expireData.data.expireTime }));
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  /**
   * 获取验证码
   */
  const getCode = () => {
    if (count) return;

    registerForm.validateFields(['registerForm.phone']).then((res) => {
      const phone = res['registerForm.phone'] as string;

      let params: GetSmsCodeParams = {
        phone,
      };

      dispatch(getSmsCode(params)).then(() => {
        setCount(60);

        Toast.show({
          icon: 'success',
          content: '验证码发送成功，请注意查收',
          position: 'top',
        });
      });
    });
  };

  // 倒计时
  useEffect(() => {
    if (count) {
      setTimeout(() => {
        setCount((prevState) => prevState - 1);
      }, 1000);
    } else {
    }
  }, [count]);

  // 倒计时提示
  useEffect(() => {
    if (count) {
      setCodeTip(`重新获取 ${count}S`);
    } else {
      setCodeTip('重新获取');
    }
  }, [count]);

  /**
   * 注册
   */
  const register = (data: RegisterForm) => {
    let params: {
      [propName: string]: string | undefined;
    } = {};

    Object.keys(data).forEach((key) => {
      params[key.split('.')[1]] = data[key as keyof RegisterForm];
    });

    $http.post('api/Home/Register', params).then(() => {
      Toast.show({
        icon: 'success',
        content: '注册成功，请登录',
        position: 'top',
      });

      setType('login');
    });
  };

  const loginFormJSX = (
    <Form
      footer={
        <Button block type="submit" color="primary" loading={loading}>
          提交
        </Button>
      }
      onFinish={login}
    >
      <Form.Item name={'loginForm.phone'} rules={loginRules['loginForm.phone']}>
        <div>
          <Input placeholder={'请输入手机号'} />
          <div className={'pre-icon'}>
            <img src={require('@images/login/userHead.png').default} alt="" />
          </div>
        </div>
      </Form.Item>

      <Form.Item name={'loginForm.password'} rules={loginRules['loginForm.password']}>
        <div>
          <Input placeholder={'请输入密码'} type={'password'} />
          <div className={'pre-icon'}>
            <img src={require('@images/login/lock.png').default} alt="" />
          </div>
        </div>
      </Form.Item>
    </Form>
  );

  const registerFormJSX = (
    <Form
      form={registerForm}
      footer={
        <Button block type="submit" color="primary" loading={loading}>
          提交
        </Button>
      }
      onFinish={register}
    >
      <Form.Item name={'registerForm.phone'} rules={registerRules['registerForm.phone']}>
        <div>
          <Input placeholder={'请输入手机号'} />
          <div className={'pre-icon'}>
            <img src={require('@images/login/userHead.png').default} alt="" />
          </div>
        </div>
      </Form.Item>

      <Form.Item name={'registerForm.code'} rules={registerRules['registerForm.code']}>
        <div>
          <div className={'verification-code'}>
            <Input placeholder={'请输入验证码'} />
            <Button className={'get-code'} color={'primary'} onClick={getCode}>
              {codeTip}
            </Button>
          </div>

          <div className={'pre-icon'}>
            <img src={require('@images/login/verificationCode.png').default} alt="" />
          </div>
        </div>
      </Form.Item>

      <Form.Item name={'registerForm.password'} rules={registerRules['registerForm.password']}>
        <div>
          <Input placeholder={'请输入密码'} type={'password'} />
          <div className={'pre-icon'}>
            <img src={require('@images/login/lock.png').default} alt="" />
          </div>
        </div>
      </Form.Item>

      <Form.Item name={'registerForm.enterpriseCode'}>
        <div>
          <Input placeholder={'请输入企业代码（选填）'} />
          <div className={'pre-icon'}>
            <img src={require('@images/login/companyIcon.png').default} alt="" />
          </div>
        </div>
      </Form.Item>
    </Form>
  );

  return (
    <div className={'login-page'}>
      <div className={'bg'}>
        <img src={require('@images/login/loginBg.png').default} alt="" />
      </div>

      <div className={'content'}>
        <div className={'logo'}>
          <img src={require('@images/login/logo.png').default} alt="" />
        </div>

        <div className={'form-box'}>
          <div className={'login-type'}>
            <span
              className={classNames({ active: type === 'login' })}
              onClick={() => setType('login')}
            >
              登录
            </span>
            <span
              className={classNames({ active: type === 'register' })}
              onClick={() => setType('register')}
            >
              注册
            </span>
          </div>

          <div className={classNames({ hidden: type !== 'login' })}>{loginFormJSX}</div>

          <div className={classNames({ hidden: type !== 'register' })}>{registerFormJSX}</div>
        </div>
      </div>
    </div>
  );
};

export default Login;
